// MeetingPin — a small banner pinned top-right of the chat window for the user's most imminent
// meeting: live now, already started, or starting soon. Counts down the minutes live (the ephemeral
// reminder message scrolls away, so this keeps it in view) with a Join button. Renders nothing when
// there's no imminent meeting. Sourced from /api/meetings/upcoming (polled), across all your channels.

import { useEffect, useState } from "react";
import { MeetingsApi } from "../hooks/useApi.js";
import Icon from "./Icon.jsx";
import { MeetingTopicsModal } from "./MeetingTopics.jsx";
import { useAuth } from "../auth/AuthProvider.jsx";

const SOON_MIN = 60; // pin a scheduled meeting once it's within this many minutes of starting

export default function MeetingPin({ onJoin, liveCounts, refreshSignal }) {
  const [meetings, setMeetings] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [endBusy, setEndBusy] = useState(false);
  const [agendaOpen, setAgendaOpen] = useState(false); // discussion-topics modal for the pinned meeting
  const { user } = useAuth();

  useEffect(() => {
    let alive = true;
    const load = () => MeetingsApi.upcoming().then((r) => { if (alive) setMeetings(r || []); }).catch(() => {});
    load();
    const poll = setInterval(load, 60000);   // refresh the list every minute
    const tick = setInterval(() => setNow(Date.now()), 15000); // re-tick the countdown
    return () => { alive = false; clearInterval(poll); clearInterval(tick); };
  }, []);

  // A meeting changed (rescheduled / went live / ended) — refetch immediately instead of
  // showing stale data until the next 60s poll.
  useEffect(() => {
    if (!refreshSignal) return;
    MeetingsApi.upcoming().then((r) => setMeetings(r || [])).catch(() => {});
  }, [refreshSignal]);

  // The most relevant meeting to pin: live/started first, otherwise the soonest one that is
  // either TODAY (a 3pm meeting stays in view all day) or within SOON_MIN (covers a
  // just-past-midnight meeting pinned late the evening before).
  let pin = null; let bestRank = Infinity;
  for (const m of meetings) {
    if (!m.scheduledAt) continue;
    const t = new Date(m.scheduledAt).getTime();
    if (Number.isNaN(t)) continue;
    const mins = (t - now) / 60000;
    const live = m.status === 1;
    const started = m.status !== 1 && m.status !== 2 && mins <= 0; // its time has arrived (still in window per API)
    const soon = mins > 0 && mins <= SOON_MIN;
    const today = mins > 0 && new Date(t).toDateString() === new Date(now).toDateString();
    if (!(live || started || soon || today)) continue;
    if (t < bestRank) { bestRank = t; pin = m; } // past-t (live/started) sorts ahead of upcoming
  }
  if (!pin) return null;

  const pinAt = new Date(pin.scheduledAt);
  const mins = Math.round((pinAt.getTime() - now) / 60000);
  // Live broadcasts (join/leave) beat the 60s poll → the count updates in real time.
  const participants = liveCounts?.[pin.code] ?? pin.participants ?? 0;
  const roomEmpty = participants === 0;
  const who = participants === 1 ? "1 person" : `${participants} people`;

  // "Live now" ONLY once the scheduled time has actually arrived — joining early flips the
  // meeting to Live server-side, but to people it hasn't started yet, so show the headcount
  // + countdown instead. Started = the clock has reached the start time.
  const started = mins <= 0;
  const label = started
    ? (participants > 0 ? `Live now · ${who}` : "Started")
    : participants > 0
      ? (mins < 60 ? `${who} waiting · starts in ${mins} min` : `${who} waiting · today ${pinAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`)
      : mins < 60 ? `starts in ${mins} min`
      : mins < 120 ? `starts in ${Math.floor(mins / 60)}h ${mins % 60}m`
      : `today ${pinAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  // Red pulse only when it has genuinely started (or is running past its time) — not for an
  // early joiner sitting in the waiting room.
  const urgent = started;

  // "End meeting" (creator only) exists for ONE state: the meeting has started, everyone has
  // left, but it's still within its scheduled window — so it won't auto-end yet but you're
  // done and want the notes now. Rules:
  //   • past the window (now ≥ start + duration): NO button — the server auto-ends + posts
  //     notes on its own.
  //   • people still in it: NO button — nothing to end.
  //   • started + within window + room empty: SHOW it; clicking ends it and creates the notes.
  const windowOverAt = pinAt.getTime() + (pin.durationMinutes > 0 ? pin.durationMinutes : 30) * 60000;
  const windowOver = now >= windowOverAt;
  const showEnd = pin.isCreator && started && !windowOver && roomEmpty;
  const endMeeting = async () => {
    if (endBusy) return;
    setEndBusy(true);
    try {
      await MeetingsApi.end(pin.code);
      setMeetings((ms) => ms.filter((m) => m.code !== pin.code)); // pin clears; notes card posts in the channel
    } catch { /* someone joined or it already ended — the next poll corrects the pin */ }
    setEndBusy(false);
  };

  return (
    <div className="meetpin-wrap">
      <div className={`meetpin ${urgent ? "meetpin--live" : ""}`}>
        <span className="meetpin__icon"><Icon name="phone" /></span>
        <div className="meetpin__body">
          <strong className="meetpin__title">{pin.title}</strong>
          <span className="meetpin__sub">
            {(urgent) && <span className="meetpin__dot" />}#{pin.channelName} · {label}
          </span>
        </div>
        {showEnd && (
          <button type="button" className="btn btn--soft btn--sm meetpin__end"
                  disabled={endBusy} title="End the meeting and create the AI notes"
                  onClick={endMeeting}>{endBusy ? "…" : "End meeting"}</button>
        )}
        <button type="button" className="btn btn--primary btn--sm meetpin__join"
                onClick={() => onJoin?.({ channelId: pin.channelId, code: pin.code })}>Join</button>
        {/* Prep/review the agenda right from the pin — the imminent meeting is exactly the
            one whose topics you want at hand (staff manage; modal is the shared editor). */}
        {user?.isStaff && (
          <button type="button" className="btn btn--soft btn--sm meetpin__topics" title="Discussion topics for this meeting"
                  onClick={() => setAgendaOpen(true)}>📋</button>
        )}
        <button type="button" className="btn btn--soft btn--sm meetpin__copy" title="Copy meeting link"
                onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/meet/${pin.code}`).catch(() => {})}>🔗</button>
      </div>
      {agendaOpen && (
        <MeetingTopicsModal code={pin.code} title={pin.title} canEdit={!!user?.isStaff}
          onClose={() => setAgendaOpen(false)} />
      )}
    </div>
  );
}
